import React, { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { type Map } from 'maplibre-gl';
import { type RouteHistory } from '../types/GlobalContext';

export interface ActiveRouteGeometry {
  id: string;
  polylines: Array<{
    id: string;
    coordinates: Array<[number, number]>;
    type: 'car' | 'transit' | 'walk' | 'bike' | 'other';
  }>;
}

interface EphemeralState {
  map?: Map;
  routeHistory: RouteHistory[];
  trackingMode: boolean;
  loadingItinerary: boolean;
  activeRouteGeometry: ActiveRouteGeometry | null;
}

interface EphemeralContextType {
  state: EphemeralState;
  setMap: (map: Map) => void;
  addRouteHistory: (route: RouteHistory) => void;
  clearRouteHistory: () => void;
  switchTrackingMode: () => void;
  setLoadingItinerary: (loading: boolean) => void;
  setActiveRouteGeometry: (geometry: ActiveRouteGeometry | null) => void;
}

const EphemeralContext = createContext<EphemeralContextType | null>(null);

export const EphemeralProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // État local pour les propriétés éphémères
  const [state, setState] = useState<EphemeralState>({
    map: undefined,
    routeHistory: [],
    trackingMode: false,
    loadingItinerary: false,
    activeRouteGeometry: null
  });

  const setMap = useCallback((map: Map) => {
    setState((prev) => ({ ...prev, map }));
  }, []);

  const addRouteHistory = useCallback((route: RouteHistory) => {
    setState((prev) => ({
      ...prev,
      routeHistory: [...prev.routeHistory, route]
    }));
  }, []);

  const clearRouteHistory = useCallback(() => {
    setState((prev) => ({
      ...prev,
      routeHistory: []
    }));
  }, []);

  const switchTrackingMode = useCallback(() => {
    setState((prev) => ({ ...prev, trackingMode: !prev.trackingMode }));
  }, []);

  const setLoadingItinerary = useCallback((loading: boolean) => {
    setState((prevState) => ({
      ...prevState,
      loadingItinerary: loading
    }));
  }, []);

  const setActiveRouteGeometry = useCallback((geometry: ActiveRouteGeometry | null) => {
    setState((prevState) => ({
      ...prevState,
      activeRouteGeometry: geometry
    }));
  }, []);

  const contextValue: EphemeralContextType = {
    state,
    setMap,
    addRouteHistory,
    clearRouteHistory,
    switchTrackingMode,
    setLoadingItinerary,
    setActiveRouteGeometry
  };

  return <EphemeralContext.Provider value={contextValue}>{children}</EphemeralContext.Provider>;
};

export const useEphemeral = (): EphemeralContextType => {
  const context = useContext(EphemeralContext);
  if (context == null) {
    throw new Error('useEphemeral must be used within an EphemeralProvider');
  }
  return context;
};
